import { useDraggable } from '@dnd-kit/core'
import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined'
import AttachFile from '@mui/icons-material/AttachFile'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined'
import MarkEmailUnreadOutlined from '@mui/icons-material/MarkEmailUnreadOutlined'
import Star from '@mui/icons-material/Star'
import StarBorder from '@mui/icons-material/StarBorder'
import { Box, Checkbox, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { memo } from 'react'
import type { Label, MessageSummary } from '../api/types'
import { formatDate, senderName } from '../lib/cache'
import { SenderAvatar } from '../lib/Avatar'
import { OrbitAvatar } from '../lib/Orbit'
import { useTokens } from '../theme'

interface Props {
  m: MessageSummary
  labels: Map<string, Label>
  selected: boolean
  focused: boolean
  open: boolean
  onOpen: (m: MessageSummary) => void
  onToggle: (id: string) => void
  onStar: (m: MessageSummary) => void
  onHover: (id: string) => void
  onQuick: (m: MessageSummary, action: 'archive' | 'trash' | 'read' | 'unread') => void
  canArchive: boolean
}

const STAR = '#f4b400'

/** Two-line row: sender avatar (becomes the checkbox on hover), sender and time on top, subject and snippet below. */
export const MessageRow = memo(function MessageRow({ m, labels, selected, focused, open, onOpen, onToggle, onStar, onHover, onQuick, canArchive }: Props) {
  const t = useTokens()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `msg:${m.id}` })
  const unread = !m.seen
  const stop = { onClick: (e: { stopPropagation: () => void }) => e.stopPropagation(), onPointerDown: (e: { stopPropagation: () => void }) => e.stopPropagation() }
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="row"
      aria-selected={selected || open}
      data-testid="message-row"
      onClick={() => onOpen(m)}
      onMouseEnter={() => onHover(m.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(m) }}
      sx={{
        position: 'relative', display: 'flex', alignItems: 'center', gap: 1.75, pl: 2.5, pr: 1.5, py: 1, minHeight: 64, cursor: 'pointer', opacity: isDragging ? 0.4 : 1,
        bgcolor: selected || open ? t.selected : 'background.paper', transition: 'background-color 120ms',
        outline: focused ? '2px solid' : 'none', outlineColor: 'primary.main', outlineOffset: -2,
        '&::after': { content: '""', position: 'absolute', left: 72, right: 0, bottom: 0, height: '1px', bgcolor: 'divider' },
        '&:hover': { bgcolor: selected || open ? t.selected : t.canvas },
        '&:hover .row-date, &:focus-within .row-date': { display: 'none' }, '&:hover .row-actions, &:focus-within .row-actions': { display: 'flex' },
        '&:hover .row-avatar, &:focus-within .row-avatar': { opacity: 0 }, '&:hover .row-check, &:focus-within .row-check': { opacity: 1 },
        '&:hover .row-star, &:focus-within .row-star': { opacity: 1 },
      }}
    >
      <OrbitAvatar unread={unread} size={40}>
        <Box className="row-avatar" sx={{ opacity: selected ? 0 : 1, transition: 'opacity 120ms' }}><SenderAvatar name={m.from.name} address={m.from.address} size={40} /></Box>
        <Checkbox className="row-check" size="small" checked={selected} {...stop} onChange={() => onToggle(m.id)} slotProps={{ input: { 'aria-label': `Select ${m.subject ?? 'message'}` } }}
          sx={{ position: 'absolute', inset: 0, m: 0, p: 0, borderRadius: '50%', opacity: selected ? 1 : 0, transition: 'opacity 120ms', '&:focus-within': { opacity: 1 } }} />
        {unread && <Box component="span" role="img" aria-label="Unread" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }} />}
      </OrbitAvatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontWeight: unread ? 800 : 600, letterSpacing: '-0.005em' }}>
            {m.draft ? <Box component="span" sx={{ color: 'error.main' }}>Draft</Box> : senderName(m)}
            {m.threadCount > 1 && <Box component="span" className="tabular" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 12, ml: 0.75 }}>{m.threadCount}</Box>}
          </Typography>
          {m.hasAttachment && <AttachFile aria-label="Has attachment" sx={{ fontSize: 15, color: 'text.secondary', alignSelf: 'center', transform: 'rotate(45deg)' }} />}
          <Typography className="row-date" variant="caption" component="time" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0, fontWeight: unread ? 700 : 500, color: unread ? 'text.primary' : 'text.secondary' }}>{formatDate(m.date)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '1px', pr: 3 }}>
          {m.labelIds.map((id) => { const l = labels.get(id); return l ? <Chip key={id} label={l.name} sx={{ bgcolor: l.color, color: '#fff', height: 18, fontSize: 11, flexShrink: 0, borderRadius: '6px' }} /> : null })}
          <Typography noWrap sx={{ minWidth: 0, fontWeight: unread ? 700 : 400, color: unread ? 'text.primary' : 'text.secondary' }}>
            {m.subject || '(no subject)'}
            {m.snippet && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>{' – '}{m.snippet}</Box>}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ position: 'absolute', right: 8, top: 6, display: 'flex', alignItems: 'center' }}>
        <Box className="row-actions" {...stop} sx={{ display: 'none', alignItems: 'center', bgcolor: selected || open ? t.selected : t.canvas, borderRadius: '10px' }}>
          {canArchive && <Tooltip title="Archive"><IconButton size="small" aria-label="Archive" onClick={() => onQuick(m, 'archive')}><ArchiveOutlined fontSize="small" /></IconButton></Tooltip>}
          <Tooltip title="Delete"><IconButton size="small" aria-label="Delete" onClick={() => onQuick(m, 'trash')}><DeleteOutlined fontSize="small" /></IconButton></Tooltip>
          <Tooltip title={unread ? 'Mark as read' : 'Mark as unread'}><IconButton size="small" aria-label={unread ? 'Mark as read' : 'Mark as unread'} onClick={() => onQuick(m, unread ? 'read' : 'unread')}>{unread ? <MarkEmailReadOutlined fontSize="small" /> : <MarkEmailUnreadOutlined fontSize="small" />}</IconButton></Tooltip>
        </Box>
      </Box>
      <IconButton className="row-star" size="small" aria-label={m.starred ? 'Unstar' : 'Star'} {...stop} onClick={(e) => { e.stopPropagation(); onStar(m) }}
        sx={{ position: 'absolute', right: 8, bottom: 4, opacity: m.starred ? 1 : 0, color: m.starred ? STAR : 'text.secondary', transition: 'opacity 120ms', '&:focus-visible': { opacity: 1 } }}>
        {m.starred ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
      </IconButton>
    </Box>
  )
})
